// ── Types ────────────────────────────────────────────────────────────────────

export type TestSafety = 'safe' | 'write' | 'dangerous';

export interface TestInput {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object';
  label: string;
  required: boolean;
  defaultResolver?: string; // key into smart-defaults resolver
  defaultValue?: any;
  description?: string;     // tooltip description for the input
}

export interface DiagnosticTestDefinition {
  id: string;              // e.g. 'validation.validatePhoneNumber'
  domain: string;          // e.g. 'validation'
  name: string;            // human-readable name
  safety: TestSafety;
  inputs: TestInput[];
  dependencies?: string[]; // test IDs that must pass first
  timeoutMs?: number;
  enabled: boolean;
  toolName?: string;       // Tool from @common/tools that this test validates (e.g. 'getChatsAndContactsTool')
  paginationEnabled?: boolean; // Show Next/Prev buttons for pagination
  extensionOnly?: boolean; // Tool only works inside WhatsApp Web (mutates local WA UI/store state);
                           // it returns EXTENSION_ONLY over MQTT by design. Server-mode diagnostics
                           // mark it "skipped (extension-only)" instead of counting a failure.
}

export interface DiagnosticTestResult {
  id: string;
  name: string;
  domain: string;
  status: 'pass' | 'fail' | 'error' | 'skipped' | 'running';
  duration: number;
  response?: any;
  error?: { message: string; stack?: string };
  safety: TestSafety;
  inputs?: Record<string, any>;
  chatType?: 'contact' | 'group' | 'newsletter';
  toolName?: string;
}

export interface DiagnosticRunOptions {
  testIds?: string[];           // empty/undefined = run all
  safetyLevels?: TestSafety[];  // default: ['safe']
  domains?: string[];
  inputOverrides?: Record<string, Record<string, any>>;
  timeoutMs?: number;           // default per-test timeout
  trimResponse?: boolean;       // default: true — set false to skip sanitizeResponse()
}

export interface DiagnosticRunResult {
  results: DiagnosticTestResult[];
  summary: {
    total: number;
    passed: number;
    failed: number;
    errored: number;
    skipped: number;
  };
  durationMs: number;
}

// ── Test Registry ────────────────────────────────────────────────────────────

export const diagnosticTests: DiagnosticTestDefinition[] = [
  // ── Validation ──────────────────────────────────────────────────────────
  {
    id: 'validation.validateChatId',
    domain: 'validation',
    name: 'Validate Phone Number or Chat ID',
    safety: 'safe',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waValidatePhoneNumberOrChatIdTool',
  },
  {
    id: 'validation.getLinkPreview',
    domain: 'validation',
    name: 'Get Link Preview',
    safety: 'safe',
    inputs: [
      { name: 'content', type: 'string', label: 'URL', required: true, defaultValue: 'https://google.com' },
    ],
    enabled: true,
    toolName: 'waGetLinkPreviewTool',
  },

  // ── Chat ────────────────────────────────────────────────────────────────
  {
    id: 'chat.getProfilePicUrl',
    domain: 'chat',
    name: 'Get Profile Pic URL',
    safety: 'safe',
    inputs: [
      { name: 'chatIds', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waGetProfilePicUrlTool',
  },
  {
    id: 'chat.getChatObjectByChatId',
    domain: 'chat',
    name: 'Get Chat Object By Chat ID',
    safety: 'safe',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waGetChatObjectByChatIdTool',
  },
  {
    id: 'chat.getChatParticipants',
    domain: 'chat',
    name: 'Get Chat Participants',
    safety: 'safe',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'firstGroupChatId' },
    ],
    enabled: true,
    toolName: 'waGetChatParticipantsTool',
  },

  // ── Latest Chats + Messages (composite, one round-trip) ─────────────
  {
    id: 'chat.getLatestChatsAndMessages',
    domain: 'chat',
    name: 'Get Latest Chats & Messages',
    safety: 'safe',
    timeoutMs: 60000,
    inputs: [
      { name: 'numberOfChats', type: 'number', label: 'Number of Chats', required: false, defaultValue: 50, description: 'Total chats to return (pinned included)' },
      { name: 'numberOfMessages', type: 'number', label: 'Messages per Chat', required: false, defaultValue: 20, description: 'Latest messages per chat' },
    ],
    enabled: true,
    toolName: 'waGetLatestChatsAndMessagesTool',
  },

  // ── Contact/Chat Query Tool ─────────────────────────────────────────
  // Single comprehensive test with all options for waQueryContactsAndChatsTool
  {
    id: 'chat.queryContactsAndChats',
    domain: 'chat',
    name: 'Query Contacts & Chats',
    safety: 'safe',
    timeoutMs: 120000, // 2 minutes for large data fetches (e.g., limit=-1)
    inputs: [
      // ── Data Selection ──
      { name: 'includeContacts', type: 'boolean', label: 'Include Contacts', required: false, defaultValue: true },
      { name: 'includeChats', type: 'boolean', label: 'Include Chats', required: false, defaultValue: true },
      // ── Pagination ──
      { name: 'limit', type: 'number', label: 'Limit', required: false, defaultValue: 10 },
      { name: 'offset', type: 'number', label: 'Offset', required: false, defaultValue: 0 },
      // ── Search Filter ──
      { name: 'searchToken', type: 'string', label: 'searchToken', required: false, defaultValue: '' },
      // ── Exclusion Filters (if true, exclude that type) ──
      { name: 'filterGroups', type: 'boolean', label: 'Exclude Groups', required: false, defaultValue: false },
      { name: 'filterContacts', type: 'boolean', label: 'Exclude Individual Contacts', required: false, defaultValue: false },
      { name: 'filterNewsletters', type: 'boolean', label: 'Exclude Newsletters', required: false, defaultValue: false },
      // ── Options ──
      { name: 'includeWithoutName', type: 'boolean', label: 'Include Without Name', required: false, defaultValue: false },
      { name: 'includeLastMessage', type: 'boolean', label: 'Include Last Message', required: false, defaultValue: false },
      { name: 'includeArchive', type: 'boolean', label: 'Include Archive', required: false, defaultValue: false },
    ],
    enabled: true,
    toolName: 'waQueryContactsAndChatsTool',
  },

  // ── Group ───────────────────────────────────────────────────────────────
  {
    id: 'group.create',
    domain: 'group',
    name: 'Create Group',
    safety: 'write',
    inputs: [
      { name: 'groupName', type: 'string', label: 'Group name', required: false, defaultValue: 'BT Diagnostics Group' },
      { name: 'participantChatIdsCommaSeparated', type: 'string', label: 'Participants (CSV chatIds)', required: false, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waCreateGroupTool',
  },
  {
    id: 'group.updateName',
    domain: 'group',
    name: 'Update Group Name',
    safety: 'write',
    inputs: [
      { name: 'groupId', type: 'string', label: 'Group ID', required: false, defaultResolver: 'firstGroupChatId' },
      { name: 'groupName', type: 'string', label: 'New group name', required: false, defaultValue: 'BT Diagnostics Group (Updated)' },
    ],
    enabled: true,
    toolName: 'waUpdateGroupNameTool',
  },
  {
    id: 'group.getData',
    domain: 'group',
    name: 'Get Group Data',
    safety: 'safe',
    inputs: [
      { name: 'groupId', type: 'string', label: 'Group ID', required: false, defaultResolver: 'firstGroupChatId' },
    ],
    enabled: true,
    toolName: 'waGetGroupDataTool',
  },
  {
    id: 'group.getCommonGroups',
    domain: 'group',
    name: 'Get Common Groups',
    safety: 'safe',
    inputs: [
      { name: 'contactChatId', type: 'string', label: 'Contact Chat ID', required: true, defaultResolver: 'ownChatId', description: "Contact's individual chat ID (e.g., 972548275828@c.us) — returns the groups you share with them" },
    ],
    enabled: true,
    toolName: 'waGetCommonGroupsTool',
  },
  {
    id: 'group.updateSettings',
    domain: 'group',
    name: 'Update Group Description',
    safety: 'write',
    inputs: [
      { name: 'groupId', type: 'string', label: 'Group ID', required: false, defaultResolver: 'firstGroupChatId' },
      {
        name: 'settingsDescription',
        type: 'string',
        label: 'Settings Description',
        required: false,
        defaultValue: '',
        description: 'Examples: only admins can edit group info | all members can send messages | only admins can add participants',
      },
    ],
    enabled: true,
    toolName: 'waUpdateGroupDescriptionTool',
  },
  {
    id: 'group.addParticipant',
    domain: 'group',
    name: 'Add Group Participant',
    safety: 'write',
    inputs: [
      { name: 'groupId', type: 'string', label: 'Group ID', required: false, defaultResolver: 'firstGroupChatId' },
      { name: 'participantChatId', type: 'string', label: 'Participant Chat ID', required: false, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waAddGroupParticipantTool',
  },
  {
    id: 'group.removeParticipant',
    domain: 'group',
    name: 'Remove Group Participant',
    safety: 'write',
    inputs: [
      { name: 'groupId', type: 'string', label: 'Group ID', required: false, defaultResolver: 'firstGroupChatId' },
      { name: 'participantChatId', type: 'string', label: 'Participant Chat ID', required: false, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waRemoveGroupParticipantTool',
  },
  {
    id: 'group.setAdminRights',
    domain: 'group',
    name: 'Set Group Admin Rights',
    safety: 'write',
    inputs: [
      { name: 'groupId', type: 'string', label: 'Group ID', required: false, defaultResolver: 'firstGroupChatId' },
      { name: 'participantChatId', type: 'string', label: 'Participant Chat ID', required: false, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waSetGroupAdminRightsTool',
  },
  {
    id: 'group.removeAdminRights',
    domain: 'group',
    name: 'Remove Group Admin Rights',
    safety: 'write',
    inputs: [
      { name: 'groupId', type: 'string', label: 'Group ID', required: false, defaultResolver: 'firstGroupChatId' },
      { name: 'participantChatId', type: 'string', label: 'Participant Chat ID', required: false, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waRemoveGroupAdminRightsTool',
  },
  {
    id: 'group.setPicture',
    domain: 'group',
    name: 'Set Group Picture',
    safety: 'write',
    inputs: [
      { name: 'groupId', type: 'string', label: 'Group ID', required: false, defaultResolver: 'firstGroupChatId' },
      { name: 'fileDataUrl', type: 'string', label: 'File (Upload)', required: true, defaultResolver: 'testImageDataUrl' },
    ],
    enabled: true,
    toolName: 'waSetGroupPictureTool',
  },
  {
    id: 'group.leave',
    domain: 'group',
    name: 'Leave Group',
    safety: 'write',
    inputs: [
      { name: 'groupId', type: 'string', label: 'Group ID', required: false, defaultResolver: 'firstGroupChatId' },
    ],
    enabled: true,
    toolName: 'waLeaveGroupTool',
  },

  // ── Newsletter (Channel) ──────────────────────────────────────────────
  {
    id: 'newsletter.getNewsletter',
    domain: 'newsletter',
    name: 'Get Newsletter',
    safety: 'safe',
    inputs: [
      { name: 'newsletterId', type: 'string', label: 'Newsletter ID', required: true, defaultResolver: 'firstNewsletterId', description: 'Channel/newsletter id, e.g. 1203631...@newsletter' },
    ],
    enabled: true,
    toolName: 'waGetNewsletterTool',
  },

  // ── Message Query Tool ─────────────────────────────────────────────
  // Single comprehensive test with all options for waQueryMessagesTool
  {
    id: 'message.queryMessages',
    domain: 'message',
    name: 'Query Messages',
    safety: 'safe',
    timeoutMs: 180000,
    inputs: [
      // ── Required ──
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId', description: 'WhatsApp chat ID (e.g., 972548275828@c.us)' },
      // ── Search (optional) ──
      { name: 'search', type: 'string', label: 'Search', required: false, defaultValue: '', description: 'Search text in messages' },
      // ── Pagination ──
      { name: 'limit', type: 'number', label: 'Limit', required: false, defaultValue: 20, description: 'Messages per page (default: 20)' },
      { name: 'offset', type: 'number', label: 'Offset', required: false, defaultValue: 0, description: 'Skip N messages' },
      // ── Order ──
      { name: 'order', type: 'string', label: 'Order', required: false, defaultValue: 'desc', description: 'desc: newest first (default), asc: oldest first' },
      // ── Date Filters ──
      { name: 'after', type: 'string', label: 'After', required: false, defaultValue: '', description: 'Messages after this date (ISO)' },
      { name: 'before', type: 'string', label: 'Before', required: false, defaultValue: '', description: 'Messages before this date (ISO)' },
      // ── Options ──
      { name: 'loadFromPhoneIfNeeded', type: 'boolean', label: 'Load from phone', required: false, defaultValue: false, description: 'Try to load older messages from phone if not enough in store' },
      { name: 'includeMediaContent', type: 'boolean', label: 'Media content', required: false, defaultValue: false, description: 'Include base64 media data (larger response)' },
    ],
    enabled: true,
    toolName: 'waQueryMessagesTool',
    // Enable pagination buttons in UI
    paginationEnabled: true,
  },

  // ── Load Messages From Phone Tool ──────────────────────────────────────────
  // Standalone tool to load older messages from phone
  {
    id: 'message.loadMessagesFromPhone',
    domain: 'message',
    name: 'Load Messages From Phone',
    safety: 'safe',
    timeoutMs: 60000,
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId', description: 'WhatsApp chat ID to load older messages for' },
    ],
    enabled: true,
    toolName: 'waLoadMessagesFromPhoneTool',
  },
  {
    id: 'chat.sendSeen',
    domain: 'chat',
    name: 'Send Seen',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waSendSeenTool',
  },
  {
    id: 'chat.clearMessages',
    domain: 'chat',
    name: 'Clear Messages',
    safety: 'dangerous',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId', description: 'Deletes all messages in the chat (irreversible).' },
    ],
    enabled: true,
    toolName: 'waClearMessagesTool',
  },
  {
    id: 'chat.deleteChat',
    domain: 'chat',
    name: 'Delete Chat',
    safety: 'dangerous',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId', description: 'Deletes the chat from the list (irreversible).' },
    ],
    enabled: true,
    toolName: 'waDeleteChatTool',
  },
  {
    id: 'chat.archiveChat',
    domain: 'chat',
    name: 'Archive Chat',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waArchiveChatTool',
  },
  {
    id: 'chat.unarchiveChat',
    domain: 'chat',
    name: 'Unarchive Chat',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waUnarchiveChatTool',
  },
  {
    id: 'chat.pinChat',
    domain: 'chat',
    name: 'Pin Chat',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waPinChatTool',
  },
  {
    id: 'chat.unpinChat',
    domain: 'chat',
    name: 'Unpin Chat',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waUnpinChatTool',
  },
  {
    id: 'chat.muteChat',
    domain: 'chat',
    name: 'Mute Chat',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'unmuteDate', type: 'string', label: 'Unmute Date (ISO)', required: false, defaultValue: '', description: 'ISO date string. Leave empty to mute forever.' },
    ],
    enabled: true,
    toolName: 'waMuteChatTool',
  },
  {
    id: 'chat.unmuteChat',
    domain: 'chat',
    name: 'Unmute Chat',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waUnmuteChatTool',
  },
  {
    id: 'chat.markChatUnread',
    domain: 'chat',
    name: 'Mark Chat Unread',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waMarkChatUnreadTool',
  },
  {
    id: 'chat.sendStateTyping',
    domain: 'chat',
    name: 'Send State: Typing',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waSendStateTypingTool',
  },
  {
    id: 'chat.sendStateRecording',
    domain: 'chat',
    name: 'Send State: Recording',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waSendStateRecordingTool',
  },
  {
    id: 'chat.clearState',
    domain: 'chat',
    name: 'Clear State (stop typing/recording)',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waClearStateTool',
  },
  {
    id: 'chat.getChatLabels',
    domain: 'chat',
    name: 'Get Chat Labels',
    safety: 'safe',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waGetChatLabelsTool',
  },
  {
    // #592 — list ALL native WhatsApp Business labels on the account (no inputs).
    id: 'chat.getLabels',
    domain: 'chat',
    name: 'Get All Business Labels',
    safety: 'safe',
    inputs: [],
    enabled: true,
    toolName: 'waGetLabelsTool',
  },
  {
    id: 'chat.changeLabels',
    domain: 'chat',
    name: 'Change Chat Labels',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'labelIdsCommaSeparated', type: 'string', label: 'Label IDs (CSV)', required: true, defaultValue: '', description: 'Comma-separated label IDs. WhatsApp Business only.' },
    ],
    enabled: true,
    toolName: 'waChangeLabelsTool',
  },
  {
    id: 'chat.getPinnedMessages',
    domain: 'chat',
    name: 'Get Pinned Messages',
    safety: 'safe',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waGetPinnedMessagesTool',
  },
  {
    id: 'chat.addOrEditCustomerNote',
    domain: 'chat',
    name: 'Add or Edit Customer Note',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId', description: 'Must not be a group/channel. WhatsApp Business only.' },
      { name: 'note', type: 'string', label: 'Note', required: true, defaultValue: 'Diagnostic note' },
    ],
    enabled: true,
    toolName: 'waAddOrEditCustomerNoteTool',
  },

  // ── Message Retrieval ───────────────────────────────────────────────────
  // NOTE: waGetMessagesTool and waSearchMessagesTool have been unified into waQueryMessagesTool
  // See message.queryMessages in the message domain above
  {
    id: 'message.getWaMessageByWaMessageKey',
    domain: 'message',
    name: 'Get WA Message By Key',
    safety: 'safe',
    inputs: [
      { name: 'waMessageKey', type: 'string', label: 'Message key', required: true, defaultResolver: 'latestMessageKey' },
    ],
    dependencies: ['message.queryMessages'],
    enabled: true,
    toolName: 'waGetMessageByKeyTool',
  },
  {
    id: 'message.messageStoreLookup',
    domain: 'message',
    name: 'Message Store Lookup',
    safety: 'safe',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'userMessageId', type: 'string', label: 'UserMessage._id', required: true, defaultValue: '' },
      { name: 'dueDate', type: 'string', label: 'Due Date (ISO)', required: false, defaultValue: '' },
    ],
    enabled: true,
    toolName: 'getUnloggedWaMessageKeyIfExistInStore',
  },
  {
    id: 'message.reactToMessage',
    domain: 'message',
    name: 'React To Message',
    safety: 'write',
    inputs: [
      { name: 'messageKey', type: 'string', label: 'Message key', required: true, defaultResolver: 'latestMessageKey' },
      { name: 'text', type: 'string', label: 'Reaction emoji', required: false, defaultValue: '👍' },
    ],
    dependencies: ['message.getMessages_latest'],
    enabled: true,
    toolName: 'waReactToMessageThunkTool',
  },
  {
    id: 'message.forwardMessage',
    domain: 'message',
    name: 'Forward Message',
    safety: 'write',
    inputs: [
      { name: 'messageKey', type: 'string', label: 'Message key', required: true, defaultResolver: 'latestMessageKey' },
      { name: 'toChatIds', type: 'string', label: 'Destination chat ids (comma-separated)', required: true, description: 'One or more chat ids (@c.us / @g.us / @newsletter), comma-separated.' },
    ],
    dependencies: ['message.getMessages_latest'],
    enabled: true,
    toolName: 'waForwardMessageTool',
  },
  {
    id: 'message.starMessage',
    domain: 'message',
    name: 'Star / Unstar Message',
    safety: 'write',
    inputs: [
      { name: 'messageKey', type: 'string', label: 'Message key', required: true, defaultResolver: 'latestMessageKey' },
      { name: 'star', type: 'boolean', label: 'Star (true) / unstar (false)', required: false, defaultValue: true },
    ],
    dependencies: ['message.getMessages_latest'],
    enabled: true,
    toolName: 'waStarMessageTool',
  },
  {
    id: 'message.deleteMessage',
    domain: 'message',
    name: 'Delete Message',
    safety: 'write',
    inputs: [
      { name: 'messageKey', type: 'string', label: 'Message key', required: true, defaultResolver: 'latestMessageKey' },
      { name: 'forEveryone', type: 'boolean', label: 'Delete for everyone (revoke)', required: false, defaultValue: false, description: 'true = delete for everyone (revoke, permission-gated); false = delete for me.' },
    ],
    dependencies: ['message.getMessages_latest'],
    enabled: true,
    toolName: 'waDeleteMessageTool',
  },
  {
    id: 'message.pinMessage',
    domain: 'message',
    name: 'Pin Message',
    safety: 'write',
    inputs: [
      { name: 'waMessageKey', type: 'string', label: 'Message key', required: true, defaultResolver: 'latestMessageKey' },
      { name: 'duration', type: 'number', label: 'Duration (seconds)', required: false, defaultValue: 604800, description: 'Pin expiry in seconds. Defaults to 7 days (604800).' },
    ],
    dependencies: ['message.getMessages_latest'],
    enabled: true,
    toolName: 'waPinMessageTool',
  },
  {
    id: 'message.unpinMessage',
    domain: 'message',
    name: 'Unpin Message',
    safety: 'write',
    inputs: [
      { name: 'waMessageKey', type: 'string', label: 'Message key', required: true, defaultResolver: 'latestMessageKey' },
    ],
    dependencies: ['message.getMessages_latest'],
    enabled: true,
    toolName: 'waUnpinMessageTool',
  },

  // ── Status ─────────────────────────────────────────────────────────────
  {
    id: 'status.createStatus',
    domain: 'status',
    name: 'Create Status',
    safety: 'write',
    inputs: [
      { name: 'text', type: 'string', label: 'Text', required: false, defaultValue: 'BT Diagnostics status message' },
      { name: 'fileDataUrl', type: 'string', label: 'File (Upload)', required: false },
    ],
    enabled: true,
    toolName: 'createStatusTool',
  },

  // ── Media ───────────────────────────────────────────────────────────────
  {
    id: 'media.getMessageMedia',
    domain: 'media',
    name: 'Get Message Media',
    safety: 'safe',
    inputs: [
      { name: 'msgId', type: 'string', label: 'Message ID', required: true, defaultResolver: 'latestMediaMessageKey' },
    ],
    enabled: true,
    toolName: 'waGetMessageMediaTool',
  },
  // ── Session ─────────────────────────────────────────────────────────────
  {
    id: 'session.getWaMeInfo',
    domain: 'session',
    name: 'Get WA Me Info',
    safety: 'safe',
    inputs: [],
    enabled: true,
    toolName: 'getWaMeInfoTool',
  },
  {
    id: 'session.isWhatsAppConnected',
    domain: 'session',
    name: 'Is WhatsApp Connected',
    safety: 'safe',
    inputs: [],
    enabled: true,
    toolName: 'isWhatsAppConnectedThunkTool',
  },
  {
    id: 'session.validateStores',
    domain: 'session',
    name: 'Validate Stores',
    safety: 'safe',
    inputs: [],
    enabled: true,
    toolName: 'waValidateStoresTool',
    // NOT extensionOnly: the tool has a real server mode — the engine IS a
    // WhatsApp page, so this validates the ENGINE's stores over MQTT
    // (thunk server branch -> notifier case -> page handler).
  },
  {
    id: 'message.getMessageAck',
    domain: 'message',
    name: 'Get Message Ack Data By Key',
    safety: 'safe',
    inputs: [
      { name: 'key', type: 'string', label: 'Message key', required: true, defaultResolver: 'latestMessageKey' },
    ],
    dependencies: ['message.queryMessages'],
    enabled: true,
    toolName: 'waGetMessageAckTool',
  },
  {
    id: 'session.reloadPage',
    domain: 'session',
    name: 'Reload Page',
    safety: 'dangerous',
    inputs: [
      { name: 'reason', type: 'string', label: 'Reason', required: false, defaultValue: 'diagnostics' },
    ],
    enabled: true,
    toolName: 'waReloadPageTool',
  },
  {
    id: 'session.triggerQRTimeout',
    domain: 'session',
    name: 'Trigger QR Timeout',
    safety: 'dangerous',
    inputs: [],
    enabled: true,
    toolName: 'waTriggerQRTimeoutTool',
  },
  {
    id: 'session.triggerWaLogout',
    domain: 'session',
    name: 'Trigger WA Logout',
    safety: 'dangerous',
    inputs: [],
    enabled: true,
    toolName: 'waTriggerLogoutTool',
  },

  // ── Message Sending (write tests) ──────────────────────────────────────
  {
    id: 'message.sendMessage',
    domain: 'message',
    name: 'Send Message',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'text', type: 'string', label: 'Message', required: false, defaultValue: 'BT diagnostics test', description: 'Mentions: use @Display Name (auto-resolved in groups) or @[Display Name](9725...@c.us)' },
      { name: 'secret', type: 'string', label: 'Secret', required: false, description: 'Opaque id stored on the message secret (WA messageSecret). When blank, the message _id is used.' },
      { name: 'replyTo', type: 'string', label: 'Message key (reply)', required: false, defaultResolver: 'optionalMessageKey', description: 'If provided, Send Message will run as a reply to this message key' },
      { name: 'fileDataUrl', type: 'string', label: 'File data URL / uploaded URL', required: false },
      { name: 'mediaUrl', type: 'string', label: 'File URL', required: false, description: 'URL to a file — fetched and converted to a blob so it sends like an uploaded file. Ignored when a file is uploaded above.' },
      { name: 'mediaFilename', type: 'string', label: 'File name', required: false },
      { name: 'fileMimeType', type: 'string', label: 'File MIME type', required: false },
      { name: 'pollQuestion', type: 'string', label: 'Poll question', required: false },
      { name: 'pollOptionsCsv', type: 'string', label: 'Poll options (CSV)', required: false },
      { name: 'pollAllowMultiple', type: 'boolean', label: 'Allow multiple answers', required: false, defaultValue: false },
      { name: 'linkPreview', type: 'boolean', label: 'Link preview', required: false, defaultValue: true, description: 'When ON, fetch and attach a rich preview for the first URL found in the message body. When OFF, send the message without a preview.' },
      { name: 'withTyping', type: 'boolean', label: 'Simulate typing', required: false, defaultValue: true, description: 'When ON (default), show the "typing…" indicator briefly before sending a text message. Only applies to messages with a text body.' },
      { name: 'typingSeconds', type: 'number', label: 'Typing seconds', required: false, defaultValue: 0, description: 'Extra typing duration in seconds, added on top of the built-in 0.4–0.8s random jitter. Default 0.' },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: true,
    toolName: 'sendMessageThunkTool',
  },
  {
    id: 'message.sendTextMessage',
    domain: 'message',
    name: 'Send Text (Legacy)',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'text', type: 'string', label: 'Message body', required: false, defaultValue: 'BT Diagnostics test - text message' },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: false,
    toolName: 'sendMessageThunkTool',
  },
  {
    id: 'message.sendImageMessage',
    domain: 'message',
    name: 'Send File',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'text', type: 'string', label: 'Caption', required: false, defaultValue: 'BT Diagnostics image test' },
      { name: 'fileDataUrl', type: 'string', label: 'File Data URL', required: false },
      { name: 'mediaFilename', type: 'string', label: 'File Name', required: false },
      { name: 'fileMimeType', type: 'string', label: 'File MIME Type', required: false },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: false,
    toolName: 'sendMessageThunkTool',
  },
  {
    id: 'message.sendVideoMessage',
    domain: 'message',
    name: 'Send Video',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'text', type: 'string', label: 'Caption', required: false, defaultValue: 'BT Diagnostics video test' },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: false,
    toolName: 'sendMessageThunkTool',
  },
  {
    id: 'message.sendPollMessage',
    domain: 'message',
    name: 'Send Poll',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'pollQuestion', type: 'string', label: 'Poll question', required: false, defaultValue: 'BT Diagnostics Test Poll' },
      { name: 'pollOptionsCsv', type: 'string', label: 'Poll options (CSV)', required: false, defaultValue: 'Option A,Option B' },
      { name: 'pollAllowMultiple', type: 'boolean', label: 'Allow multiple answers', required: false, defaultValue: false },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: false,
    toolName: 'sendMessageThunkTool',
  },
  {
    id: 'message.sendLinkPreviewMessage',
    domain: 'message',
    name: 'Send Link Preview',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'text', type: 'string', label: 'Message body', required: false, defaultValue: 'BT Diagnostics test - link preview: https://google.com' },
      { name: 'linkPreview', type: 'boolean', label: 'Link preview', required: false, defaultValue: true, description: 'When ON, fetch and attach a rich preview for the first URL found in the message body. When OFF, send the message without a preview.' },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: false,
    toolName: 'sendMessageThunkTool',
  },

  // ── Message Scheduling (write tests - stores in DB for later delivery) ──
  {
    id: 'schedule.scheduleMessage',
    domain: 'schedule',
    name: 'Schedule Message',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'text', type: 'string', label: 'Message body', required: false, defaultValue: 'BT Diagnostics scheduled message' },
      { name: 'fileDataUrl', type: 'string', label: 'File data URL / uploaded URL', required: false },
      { name: 'mediaUrl', type: 'string', label: 'File URL', required: false, description: 'URL to a file — fetched and converted to a blob so it sends like an uploaded file. Ignored when a file is uploaded above.' },
      { name: 'mediaFilename', type: 'string', label: 'File name', required: false },
      { name: 'fileMimeType', type: 'string', label: 'File MIME type', required: false },
      { name: 'pollQuestion', type: 'string', label: 'Poll question', required: false },
      { name: 'pollOptionsCsv', type: 'string', label: 'Poll options (CSV)', required: false },
      { name: 'pollAllowMultiple', type: 'boolean', label: 'Allow multiple answers', required: false, defaultValue: false },
      { name: 'linkPreview', type: 'boolean', label: 'Link preview', required: false, defaultValue: true, description: 'When ON, fetch and attach a rich preview for the first URL found in the message body. When OFF, send the message without a preview.' },
      { name: 'dueDate', type: 'string', label: 'Due Date (ISO)', required: true, defaultResolver: 'futureDate' },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: true,
    toolName: 'scheduleMessageTool',
  },
  {
    id: 'schedule.scheduleTextMessage',
    domain: 'schedule',
    name: 'Schedule Text (Legacy)',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'text', type: 'string', label: 'Message body', required: false, defaultValue: 'BT Diagnostics test - scheduled text message' },
      { name: 'dueDate', type: 'string', label: 'Due Date (ISO)', required: true, defaultResolver: 'futureDate' },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: false,
    toolName: 'scheduleMessageTool',
  },
  {
    id: 'schedule.scheduleImageMessage',
    domain: 'schedule',
    name: 'Schedule File',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'text', type: 'string', label: 'Caption', required: false, defaultValue: 'BT Diagnostics test - scheduled image' },
      { name: 'fileDataUrl', type: 'string', label: 'File data URL', required: false, defaultResolver: 'testImageDataUrl' },
      { name: 'mediaFilename', type: 'string', label: 'File name', required: false, defaultValue: 'diagnostic-image.png' },
      { name: 'fileMimeType', type: 'string', label: 'File MIME type', required: false, defaultValue: 'image/png' },
      { name: 'dueDate', type: 'string', label: 'Due Date (ISO)', required: true, defaultResolver: 'futureDate' },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: false,
    toolName: 'scheduleMessageTool',
  },
  {
    id: 'schedule.schedulePollMessage',
    domain: 'schedule',
    name: 'Schedule Poll',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'pollQuestion', type: 'string', label: 'Poll question', required: false, defaultValue: 'BT Diagnostics scheduled poll test' },
      { name: 'pollOptionsCsv', type: 'string', label: 'Poll options (CSV)', required: false, defaultValue: 'Option A,Option B' },
      { name: 'pollAllowMultiple', type: 'boolean', label: 'Allow multiple answers', required: false, defaultValue: false },
      { name: 'dueDate', type: 'string', label: 'Due Date (ISO)', required: true, defaultResolver: 'futureDate' },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: false,
    toolName: 'scheduleMessageTool',
  },
  {
    id: 'schedule.scheduleLinkPreviewMessage',
    domain: 'schedule',
    name: 'Schedule Link Preview',
    safety: 'write',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID', required: true, defaultResolver: 'ownChatId' },
      { name: 'text', type: 'string', label: 'Message body', required: false, defaultValue: 'BT Diagnostics scheduled link preview: https://google.com' },
      { name: 'linkPreview', type: 'boolean', label: 'Link preview', required: false, defaultValue: true, description: 'When ON, fetch and attach a rich preview for the first URL found in the message body. When OFF, send the message without a preview.' },
      { name: 'dueDate', type: 'string', label: 'Due Date (ISO)', required: true, defaultResolver: 'futureDate' },
    ],
    dependencies: ['validation.validateChatId'],
    enabled: false,
    toolName: 'scheduleMessageTool',
  },

  // ── Label ───────────────────────────────────────────────────────────────
  {
    id: 'label.updateShouldAppearChatsByList',
    domain: 'label',
    name: 'Update Should Appear Chats By List',
    safety: 'write',
    inputs: [
      { name: 'chatsList', type: 'object', label: 'Chats list', required: true, defaultValue: {} },
    ],
    enabled: true,
    toolName: 'waUpdateShouldAppearChatsByListTool',
    extensionOnly: true,
  },

  // ── Chat Navigation ────────────────────────────────────────────────────
  {
    id: 'chat.openChat',
    domain: 'chat',
    name: 'Open Chat',
    safety: 'safe',
    inputs: [
      { name: 'chatId', type: 'string', label: 'Chat ID or Phone', required: true, defaultResolver: 'ownChatId' },
    ],
    enabled: true,
    toolName: 'waOpenChatTool',
  },
];

export function getTestsByDomain(domain: string): DiagnosticTestDefinition[] {
  return diagnosticTests.filter(t => t.domain === domain && t.enabled);
}

export function getTestById(id: string): DiagnosticTestDefinition | undefined {
  return diagnosticTests.find(t => t.id === id);
}

export function getAllDomains(): string[] {
  return [...new Set(diagnosticTests.map(t => t.domain))];
}
